import { IonButton, IonIcon, useIonToast } from '@ionic/react';
import { arrowForward, folderOpenOutline } from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useImportProgress } from '../../../context/ImportProgressContext';
import { on, once, removeListener, run } from '../../../helper/electron';
import FolderList from './FolderList';
import './FolderManager.scss';
import FolderSelector from './FolderSelector';
import type { FolderManagerProps, ManagedFolder } from './types';

interface MyMusicFolder {
  id: string;
  path: string;
  name?: string;
  watching?: boolean;
  trackCount?: number;
}

export default function FolderManager({
  mode,
  onComplete,
  onSkip,
  showSkipOption = false,
  showAdvancedOptions = false,
  allowBulkOperations = false,
  title,
  description,
  maxFolders,
  className = '',
}: FolderManagerProps) {
  const { addImportJob, getActiveJobs, getJobsForPath } = useImportProgress();
  const [present] = useIonToast();

  const [folders, setFolders] = useState<ManagedFolder[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [isAddingFolder, setIsAddingFolder] = useState(false);

  // Refs for cleanup
  const isMountedRef = useRef(true);
  const importStartedRef = useRef(false);
  // Always-current state refs to avoid stale closures in global event handlers
  const foldersRef = useRef<ManagedFolder[]>([]);
  const selectedFoldersRef = useRef<Set<string>>(new Set());
  // Track recently deleted paths to avoid re-adding placeholder cards from active jobs
  const recentlyDeletedPathsRef = useRef<Map<string, number>>(new Map());
  const pickerOpenRef = useRef(false);

  useEffect(() => {
    console.log('[FolderManager] Component mounted in', mode, 'mode');
    isMountedRef.current = true;

    // Load existing folders for library/management modes
    if (mode !== 'wizard') {
      console.log('[FolderManager] Loading existing folders for mode:', mode);
      fetchExistingFolders();
    } else {
      console.log('[FolderManager] Wizard mode - not loading existing folders');
    }

    // Listen for folder status updates
    const handleStatusUpdate = (statusUpdate: any) => {
      console.log('[FolderManager] Received status update:', statusUpdate);
      if (statusUpdate && Array.isArray(statusUpdate)) {
        const updateMap = new Map(
          statusUpdate.map((s: any) => [s.folderPath, s]),
        );
        setFolders((prev) => {
          let hasChanges = false;
          const result = prev.map((folder) => {
            const update = updateMap.get(folder.path);
            if (update) {
              hasChanges = true;
              return {
                ...folder,
                changes: update.changes,
                lastUpdate: update.lastUpdate,
              };
            }
            return folder;
          });
          return hasChanges ? result : prev;
        });
      }
    };

    // Listen for status updates
    window.api?.on('folder-watcher/status-update', handleStatusUpdate);

    on('my-music/folders-status-start', () => {
      // optional: could set a global checking state
    });
    on('my-music/folders-status-complete', () => {
      // optional: clear global checking state
    });

    // Refresh folder counts after any import completes
    const handleImportProgress = (_evt: any, progress?: any) => {
      // Our helper 'on' passes only data, not event; handle both shapes
      const p = progress && progress.phase ? progress : _evt;
      if (p?.phase === 'complete' || p?.phase === 'cancelled') {
        fetchExistingFolders();
      }
    };
    on('track-import/progress', handleImportProgress as any);
    const handleLibraryRefreshNotification = () => fetchExistingFolders();
    on('library-refresh-notification', handleLibraryRefreshNotification);

    const handleLibraryRefresh = (event: any) => {
      fetchExistingFolders();
      setTimeout(() => {
        run('my-music/folders-status', { adaptive: true });
      }, 1000);
    };
    window.addEventListener('library-refresh', handleLibraryRefresh);
    on('library-refresh-notification', handleLibraryRefresh);

    return () => {
      console.log('[FolderManager] Component unmounting');
      isMountedRef.current = false;
      removeListener('my-music/get-folders');
      removeListener('my-music/delete-folder');
      removeListener('my-music/toggle-folder-watching');
      removeListener('my-music/get-folder-watching-status');
      window.api?.removeListener(
        'folder-watcher/status-update',
        handleStatusUpdate,
      );
      removeListener('my-music/folders-status');
      removeListener('my-music/folder-status');
      removeListener('my-music/folders-status-start');
      removeListener('my-music/folders-status-complete');
      removeListener('track-import/progress');
      window.removeEventListener('library-refresh', handleLibraryRefresh);
      removeListener('library-refresh-notification');
    };
  }, [mode]);

  // Keep refs in sync with state
  useEffect(() => {
    foldersRef.current = folders;
  }, [folders]);
  useEffect(() => {
    selectedFoldersRef.current = selectedFolders;
  }, [selectedFolders]);
  useEffect(() => {
    const handler = (e: any) => {
      const path = (e.detail?.path || '').replace(/\\/g, '/').toLowerCase();
      if (!path) return;
      setFolders((prev) =>
        prev.map((f) =>
          f.path.replace(/\\/g, '/').toLowerCase() === path
            ? { ...f, changes: undefined }
            : f,
        ),
      );
    };
    window.addEventListener(
      'folder-manager/clear-changes',
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        'folder-manager/clear-changes',
        handler as EventListener,
      );
    };
  }, []);

  //Cleanup expired recently deleted paths periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const EXPIRE_MS = 60000;
      let cleaned = 0;
      for (const [
        path,
        timestamp,
      ] of recentlyDeletedPathsRef.current.entries()) {
        if (now - timestamp > EXPIRE_MS) {
          recentlyDeletedPathsRef.current.delete(path);
          cleaned++;
        }
      }
    }, 30000);

    return () => clearInterval(cleanupInterval);
  }, []);

  const fetchExistingFolders = useCallback(() => {
    console.log('[FolderManager] Fetching existing folders for mode:', mode);
    once('my-music/get-folders', (data: MyMusicFolder[]) => {
      console.log('[FolderManager] Folders received:', data);
      console.log('[FolderManager] Number of folders:', data?.length || 0);
      const normalize = (p: string) => (p || '').replace(/\\/g, '/');
      const nowTs = Date.now();
      const SUPPRESS_MS = 60000;
      const incomingByPath = new Map(
        (data || []).map((folder) => [
          normalize(folder.path),
          {
            id: folder.id,
            path: folder.path,
            name: folder.name || folder.path.split('/').pop() || folder.path,
            status: 'complete' as const,
            watching: folder.watching || false,
            trackCount: folder.trackCount || 0,
          },
        ]),
      );

      setFolders((prev) => {
        const result: ManagedFolder[] = [];
        const seen = new Set<string>();
        // Update existing entries in-place (keep order)
        prev.forEach((existing) => {
          const key = normalize(existing.path);
          const isRecentlyDeleted =
            recentlyDeletedPathsRef.current.has(key) &&
            nowTs - (recentlyDeletedPathsRef.current.get(key) || 0) <
              SUPPRESS_MS;
          if (isRecentlyDeleted) return;
          const inc = incomingByPath.get(key);
          if (inc) {
            seen.add(key);
            result.push({
              ...existing,
              id: inc.id || existing.id,
              name: inc.name || existing.name,
              watching:
                typeof inc.watching === 'boolean'
                  ? inc.watching
                  : existing.watching,
              trackCount:
                typeof inc.trackCount === 'number'
                  ? inc.trackCount
                  : existing.trackCount,
            } as any);
          } else {
            // Not in incoming; keep it (placeholders or in-flight jobs/deletions) unless just deleted
            result.push(existing);
          }
        });
        // Append any new incoming paths that weren't present
        incomingByPath.forEach((folder, key) => {
          if (!seen.has(key)) {
            const isRecentlyDeleted =
              recentlyDeletedPathsRef.current.has(key) &&
              nowTs - (recentlyDeletedPathsRef.current.get(key) || 0) <
                SUPPRESS_MS;
            if (!isRecentlyDeleted) {
              result.push(folder as ManagedFolder);
            }
          }
        });
        console.log('[FolderManager] Merged folders (stable order):', result);
        return result;
      });

      // Re-bind any active jobs to ensure cards show progress after navigation.
      // If a job exists for a path that isn't yet persisted as a folder,
      // add a lightweight placeholder so users still see the running import.
      try {
        const active = getActiveJobs?.() || [];
        if (active.length > 0) {
          setFolders((prev) => {
            const byPath = new Map(prev.map((f) => [normalize(f.path), f]));
            const newPlaceholders: ManagedFolder[] = [];

            active.forEach((job: any) => {
              const jobPath = normalize(job.path || '');
              if (!jobPath || recentlyDeletedPathsRef.current.has(jobPath))
                return;
              const existing = byPath.get(jobPath);
              if (existing) {
                existing.status = 'importing';
              } else {
                newPlaceholders.push({
                  id: `job:${jobPath}`,
                  path: jobPath,
                  name: job.name || jobPath.split('/').pop() || jobPath,
                  status: 'importing',
                  watching: false,
                  trackCount: 0,
                } as any);
              }
            });
            return newPlaceholders.length > 0
              ? [...Array.from(byPath.values()), ...newPlaceholders]
              : prev;
          });
        }
      } catch {}

      // Auto-select all folders initially for wizard mode
      if (mode === 'wizard') {
        const folderIds = Array.from(incomingByPath.values()).map((f) => f.id);
        setSelectedFolders(new Set(folderIds));
      }
    });
    run('my-music/get-folders');
  }, [mode, getActiveJobs]);

  const handleAddFolder = () => {
    if (pickerOpenRef.current) {
      return;
    }
    pickerOpenRef.current = true;
    setIsAddingFolder(true);

    const preflightOnce = (pathToScan: string) =>
      new Promise<any>((resolve) => {
        once('my-music/preflight-scan', (pre: any) => resolve(pre));
        run('my-music/preflight-scan', { path: pathToScan });
      });

    const normalizePath = (p: string) =>
      (p || '').replace(/\\/g, '/').toLowerCase();

    const getPersistedPaths = (): Promise<Set<string>> =>
      new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(new Set()), 1500);
        once('my-music/get-folders', (data: MyMusicFolder[]) => {
          clearTimeout(timeout);
          resolve(new Set((data || []).map((f) => normalizePath(f.path))));
        });
        run('my-music/get-folders');
      });

    const currentlyProcessingPaths = new Set<string>();

    const processAllPaths = async (folderPaths: string[]) => {
      const newFolders: ManagedFolder[] = [];
      const jobsToAdd: any[] = [];
      const persistedPaths = await getPersistedPaths();
      const duplicateNames: string[] = [];

      for (const folderPath of folderPaths) {
        const folderName = folderPath.split('/').pop() || folderPath;
        const normalizedNew = normalizePath(folderPath);

        if (currentlyProcessingPaths.has(normalizedNew)) continue;

        const existsInList = foldersRef.current.some(
          (f) => normalizePath(f.path) === normalizedNew,
        );
        const existingJobs = getJobsForPath(folderPath);
        const existsAsJob = existingJobs.some(
          (j) =>
            j.status === 'queued' ||
            j.status === 'importing' ||
            j.status === 'paused' ||
            j.status === 'cancelling',
        );
        const existsInBackend = persistedPaths.has(normalizedNew);

        if (existsInList || existsAsJob || existsInBackend) {
          console.log('[FolderManager] Skipping duplicate:', folderPath);
          duplicateNames.push(folderName);
          continue;
        }
        try {
          const preflight = await preflightOnce(folderPath);
          if (preflight.ok && preflight.musicFiles === 0) {
            present({
              message: `${folderName}: No music files found in this folder`,
              duration: 3000,
              cssClass: 'custom-toast-warning',
            });
            continue;
          }
        } catch (err) {
          console.warn('[FolderManager] Preflight scan failed:', err);
        }

        currentlyProcessingPaths.add(normalizedNew);

        const newFolder: ManagedFolder = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          path: folderPath,
          name: folderName,
          status: 'importing',
        };

        newFolders.push(newFolder);
        jobsToAdd.push({
          type: 'folder',
          name: folderName,
          path: folderPath,
          status: 'queued',
          progress: {
            phase: 'queued',
            totalTracks: 0,
            processedTracks: 0,
            message: 'Queued for import...',
          },
        });
      }
      if (duplicateNames.length > 0) {
        const names = duplicateNames.join(', ');
        present({
          message:
            duplicateNames.length === 1
              ? `"${names}" is already in your library`
              : `Already in your library: ${names}`,
          duration: 4000,
          color: 'warning',
        });
      }

      if (newFolders.length > 0) {
        setFolders((prev) => [...newFolders, ...prev]);
        setSelectedFolders((prev) => {
          const updated = new Set(prev);
          newFolders.forEach((f) => updated.add(f.id));
          return updated;
        });

        jobsToAdd.forEach((job) => addImportJob(job));
      }
    };

    once('my-music/select-folder', async (response: any) => {
      try {
        const paths: string[] = Array.isArray(response?.folders)
          ? response.folders.map((f: any) => f.path)
          : response?.folder?.path
            ? [response.folder.path]
            : [];
        await processAllPaths(paths);
      } catch (error) {
        console.error('[FolderManager] Error processing folders:', error);
      } finally {
        pickerOpenRef.current = false;
        setIsAddingFolder(false);
        currentlyProcessingPaths.clear();
      }
    });

    run('my-music/select-folder');
  };

  // Allow top-toolbar Add Folder button to trigger this via a custom event
  useEffect(() => {
    const listener = () => handleAddFolder();
    window.addEventListener(
      'folder-manager/add-folder',
      listener as unknown as EventListener,
    );
    const selectAll = () => {
      const currentFolders = foldersRef.current;
      const all = new Set(currentFolders.map((f) => f.id));
      setSelectedFolders(all);
      const paths = currentFolders.map((f) => f.path);
      window.dispatchEvent(
        new CustomEvent('folder-manager/selection-changed', {
          detail: { selected: all.size, total: currentFolders.length, paths },
        }),
      );
    };
    const deselectAll = () => {
      const currentFolders = foldersRef.current;
      const empty = new Set<string>();
      setSelectedFolders(empty);
      window.dispatchEvent(
        new CustomEvent('folder-manager/selection-changed', {
          detail: { selected: 0, total: currentFolders.length, paths: [] },
        }),
      );
    };
    const removeSelected = () => {
      const ids = Array.from(selectedFoldersRef.current);
      if (!ids.length) return;
      const currentFolders = foldersRef.current;
      const normalize = (p: string) => (p || '').replace(/\\/g, '/');
      const toRemove = currentFolders.filter((f) => ids.includes(f.id));
      const paths = toRemove.map((f) => normalize(f.path));

      if (!paths.length) return;

      // No toast; row will show "Removing…" and progress will reflect in banner

      // Mark as deleting; don't remove until confirmed. Suppress reappearance markers.
      const now = Date.now();
      setFolders((prev) =>
        prev.map((f) => {
          if (ids.includes(f.id)) {
            if (f.path)
              recentlyDeletedPathsRef.current.set(normalize(f.path), now);
            return { ...f, deleting: true } as any;
          }
          return f;
        }),
      );
      // Keep selection for visual context; we can clear after success if desired

      const doBulkDelete = (deletePaths: string[], deleteIds: string[]) => {
        // Listen for completion once and force-refresh
        let responded = false;
        once('my-music/delete-folders-bulk', (response: any) => {
          responded = true;
          try {
            // Map per-path counters to in-flight rows before refetch removes them
            if (response?.perPath && Array.isArray(response.perPath)) {
              const byPath: Record<
                string,
                { toDelete: number; deleted: number }
              > = {};
              response.perPath.forEach((p: any) => {
                if (p?.path)
                  byPath[(p.path as string).replace(/\\/g, '/')] = {
                    toDelete: Number(p.toDelete || 0),
                    deleted: Number(p.deleted || 0),
                  };
              });
              setFolders((prev) =>
                prev.map((f) => {
                  const info = byPath[(f.path || '').replace(/\\/g, '/')];
                  return info
                    ? ({
                        ...f,
                        toDelete: info.toDelete,
                        deletedCount: info.deleted,
                      } as any)
                    : f;
                }),
              );
            }
            fetchExistingFolders();
            window.dispatchEvent(
              new CustomEvent('library-refresh', {
                detail: { reason: 'folders-deleted-bulk' },
              }),
            );
            if (response && response.success) {
              // Clear selection now that deletion completed
              setSelectedFolders(new Set());
            } else {
              present({
                message: 'Folder removal failed; retrying individually…',
                duration: 2000,
                color: 'warning',
              });
              // Fallback: delete per-folder by id
              deleteIds.forEach((folderId) =>
                run('my-music/delete-folder', { folderId }),
              );
            }
          } catch {}
        });

        // Safety fallback timeout if no reply comes back
        setTimeout(() => {
          if (responded) return;
          deleteIds.forEach((folderId) =>
            run('my-music/delete-folder', { folderId }),
          );
          try {
            // Refresh after fallback attempt
            setTimeout(() => fetchExistingFolders(), 1000);
          } catch {}
        }, 2000);

        // Fire bulk delete
        run('my-music/delete-folders-bulk', { paths: deletePaths });
      };

      // If there are active imports for any selected folder, confirm cancel
      try {
        const activeJobs = getActiveJobs?.() || [];
        const normalizeLc = (p: string) => normalize(p).toLowerCase();
        const selectedPathsLc = new Set(paths.map((p) => normalizeLc(p)));
        const jobIdsToCancel = activeJobs
          .filter(
            (j: any) => j.path && selectedPathsLc.has(normalizeLc(j.path)),
          )
          .map((j: any) => j.id);
        if (jobIdsToCancel.length) {
          const removeThenDelete = window.confirm(
            'Some selected folders are currently importing.\n\nCancel those imports and remove any tracks already imported from this run before deleting the folders?\n\nClick OK to cancel & remove imported. Click Cancel to delete folders only.',
          );
          if (removeThenDelete) {
            jobIdsToCancel.forEach((jobId) =>
              window.dispatchEvent(
                new CustomEvent('track-import/cancel', {
                  detail: { id: jobId, removeImported: true },
                }),
              ),
            );
          }
          // Proceed to delete regardless of choice
          doBulkDelete(paths, ids);
          return;
        }
      } catch {}

      doBulkDelete(paths, ids);
    };
    const syncSelected = () => {
      const currentFolders = foldersRef.current;
      const currentSelected = selectedFoldersRef.current;
      const paths = currentFolders
        .filter((f) => currentSelected.has(f.id))
        .map((f) => f.path);
      if (paths.length) run('my-music/sync-folders-selected', { paths });
    };
    window.addEventListener(
      'folder-manager/select-all',
      selectAll as unknown as EventListener,
    );
    window.addEventListener(
      'folder-manager/deselect-all',
      deselectAll as unknown as EventListener,
    );
    window.addEventListener(
      'folder-manager/remove-selected',
      removeSelected as unknown as EventListener,
    );
    window.addEventListener(
      'folder-manager/sync-selected',
      syncSelected as unknown as EventListener,
    );
    return () => {
      window.removeEventListener(
        'folder-manager/add-folder',
        listener as unknown as EventListener,
      );
      window.removeEventListener(
        'folder-manager/select-all',
        selectAll as unknown as EventListener,
      );
      window.removeEventListener(
        'folder-manager/deselect-all',
        deselectAll as unknown as EventListener,
      );
      window.removeEventListener(
        'folder-manager/remove-selected',
        removeSelected as unknown as EventListener,
      );
      window.removeEventListener(
        'folder-manager/sync-selected',
        syncSelected as unknown as EventListener,
      );
    };
  }, [handleAddFolder]);

  const handleRemoveFolder = (folderId: string) => {
    const toRemove = folders.find((f) => f.id === folderId);

    setFolders((prev) => {
      const folderToRemove = prev.find((f) => f.id === folderId);
      if (folderToRemove?.path) {
        // mark path as recently deleted to avoid placeholder resurrection
        recentlyDeletedPathsRef.current.set(
          folderToRemove.path.replace(/\\/g, '/'),
          Date.now(),
        );
      }

      // In wizard mode, immediately remove from local state
      if (mode === 'wizard') {
        return prev.filter((f) => f.id !== folderId);
      }

      // Flag as deleting and initialize counters; keep visible until confirmed
      return prev.map((f) =>
        f.id === folderId
          ? ({ ...f, deleting: true, toDelete: 0, deletedCount: 0 } as any)
          : f,
      );
    });
    setSelectedFolders((prev) => {
      const newSelected = new Set(prev);
      newSelected.delete(folderId);
      return newSelected;
    });

    // In wizard mode, cancel any active import jobs for this folder
    if (mode === 'wizard' && toRemove?.path) {
      try {
        const activeJobs = getActiveJobs?.() || [];
        const normalizeLc = (p: string) =>
          (p || '').replace(/\\/g, '/').toLowerCase();
        const folderPathLc = normalizeLc(toRemove.path);
        const jobIdsToCancel = activeJobs
          .filter((j: any) => j.path && normalizeLc(j.path) === folderPathLc)
          .map((j: any) => j.id);
        jobIdsToCancel.forEach((jobId) =>
          window.dispatchEvent(
            new CustomEvent('track-import/cancel', {
              detail: { id: jobId, removeImported: false },
            }),
          ),
        );
      } catch {}
    }

    // For library/management modes, also delete from backend
    if (mode !== 'wizard') {
      once('my-music/delete-folder', (resp: any) => {
        try {
          setFolders((prev) => prev.filter((f) => f.id !== folderId));
          // Optionally bump a banner / refresh
          window.dispatchEvent(
            new CustomEvent('library-refresh', {
              detail: { reason: 'folder-deleted-single' },
            }),
          );
        } catch {}
      });
      run('my-music/delete-folder', { folderId });
    }
  };

  const handleToggleWatching = (folderId: string, enabled: boolean) => {
    console.log(
      `🔧 [FolderManager] Toggling watching for folder ${folderId} to ${enabled}`,
    );

    once('my-music/toggle-folder-watching', (response: any) => {
      console.log(`🔧 [FolderManager] Received toggle response:`, response);
      if (response.success) {
        // Update the folder's watching status in the local state
        setFolders((prev) =>
          prev.map((folder) =>
            folder.id === folderId ? { ...folder, watching: enabled } : folder,
          ),
        );

        console.log(
          `✅ [FolderManager] Successfully toggled watching for folder ${folderId}`,
        );
      } else {
        console.error(
          `❌ [FolderManager] Failed to toggle watching for folder ${folderId}:`,
          response.error,
        );
      }
    });

    run('my-music/toggle-folder-watching', { folderId, enabled });
  };

  const handleRescanFolder = (folderId: string) => {
    console.log('[FolderManager] Rescanning folder:', folderId);

    const folderToRescan = folders.find((f) => f.id === folderId);
    if (!folderToRescan) {
      console.warn('[FolderManager] Folder not found for rescan:', folderId);
      return;
    }

    // Create a proper import job for this rescan
    const jobId = addImportJob({
      type: 'folder',
      name: folderToRescan.name,
      path: folderToRescan.path,
      status: 'queued',
      progress: {
        phase: 'scanning',
        totalTracks: 0,
        processedTracks: 0,
        message: 'Preparing to rescan folder...',
      },
    });

    console.log(
      '[FolderManager] Created rescan job:',
      jobId,
      'for folder:',
      folderToRescan.path,
    );
  };
  useEffect(() => {
    const handleRefreshStatus = (event: CustomEvent) => {
      const { changedPaths, changes } = event.detail || {};
      if (!changedPaths || changedPaths.length === 0) {
        return;
      }
      const normalizePath = (p: string) => p.replace(/\\/g, '/').toLowerCase();
      const normalizedChangedPaths = new Set(
        changedPaths.map((p: string) => normalizePath(p)),
      );
      setFolders((prev) => {
        const updated: ManagedFolder[] = prev.map((folder) => {
          const normalizedFolderPath = normalizePath(folder.path);
          const isChanged = normalizedChangedPaths.has(normalizedFolderPath);
          if (isChanged) {
            const folderChanges = changes?.[normalizedFolderPath];
            const validChanges = {
              added: folderChanges?.added || 0,
              deleted: folderChanges?.deleted || 0,
              total: folderChanges?.total || 0,
            };
            return {
              ...folder,
              changes: validChanges,
            } as ManagedFolder;
          }
          return folder;
        });
        return updated;
      });
    };
    window.addEventListener(
      'folder-manager/refresh-status',
      handleRefreshStatus as EventListener,
    );
    return () => {
      window.removeEventListener(
        'folder-manager/refresh-status',
        handleRefreshStatus as EventListener,
      );
    };
  }, []);

  const startImport = async () => {
    console.log('[FolderManager] Starting import...');

    if (!isMountedRef.current || importStartedRef.current) {
      console.warn(
        '[FolderManager] Import already in progress or component unmounted',
      );
      return;
    }

    if (selectedFolders.size === 0) {
      console.warn('[FolderManager] No folders selected');
      return;
    }

    const selectedFoldersList = folders.filter((f) =>
      selectedFolders.has(f.id),
    );
    console.log('[FolderManager] Importing folders:', selectedFoldersList);

    importStartedRef.current = true;
    setIsAddingFolder(true);

    // Set initial progress
    const initialProgress: any = {
      // Changed to any as ImportProgressType is removed
      phase: 'scanning',
      source: {
        type: 'folder',
        name:
          selectedFolders.size === 1
            ? selectedFoldersList[0].name
            : `${selectedFolders.size} Folders`,
        paths: selectedFoldersList.map((f) => f.path),
      },
      totalTracks: 0,
      processedTracks: 0,
      message: 'Scanning folders and preparing import...',
    };

    // Remove any existing listener
    removeListener('track-import/progress');

    // Add flag to track if we've already processed completion
    let completionProcessed = false;

    // Set up progress listener
    const handleProgress = (progressData: any) => {
      // Changed to any as ImportProgressType is removed
      console.log('[FolderManager] Progress received:', progressData);

      if (!isMountedRef.current) {
        console.warn('[FolderManager] Component unmounted, ignoring progress');
        return;
      }

      // The progress UI is now handled by FolderList, so we just update the state
      // setProgress(progressData); // This line was removed as per the edit hint

      if (progressData.phase === 'complete' && !completionProcessed) {
        completionProcessed = true; // Mark as processed
        setIsAddingFolder(false); // Hide the adding folder indicator

        console.log(
          '[FolderManager] Import complete with progress data:',
          progressData,
        );
        const totalImported =
          (progressData.created || 0) + (progressData.merged || 0);

        console.log(
          '[FolderManager] Calculated totalImported:',
          totalImported,
          'from created:',
          progressData.created,
          'merged:',
          progressData.merged,
        );

        setTimeout(() => {
          if (onComplete) {
            // Trigger library refresh when folder import completes
            const refreshEvent = new CustomEvent('library-refresh', {
              detail: { reason: 'folder-import-completed' },
            });
            window.dispatchEvent(refreshEvent);

            console.log(
              '[FolderManager] Calling onComplete with totalImported:',
              totalImported,
            );
            onComplete(totalImported);
          }
        }, 3000);

        removeListener('track-import/progress');
      } else if (progressData.phase === 'complete' && completionProcessed) {
        console.log(
          '[FolderManager] Ignoring duplicate completion message:',
          progressData,
        );
      } else if (progressData.phase === 'error') {
        console.error('[FolderManager] Import error:', progressData.error);
        setIsAddingFolder(false); // Hide the adding folder indicator on error
        removeListener('track-import/progress');
      }
    };

    // on('track-import/progress', handleProgress); // This line was removed as per the edit hint

    // Use the unified TrackImportService
    const sourceData = {
      source: {
        type: 'folder' as const,
        name:
          selectedFolders.size === 1
            ? selectedFoldersList[0].name
            : `${selectedFolders.size} Folders`,
        paths: selectedFoldersList.map((f) => f.path),
      },
    };
    run('track-import/import-from-source', sourceData);
  };

  const startRescan = async () => {
    console.log('[FolderManager] Starting rescan...');

    if (!isMountedRef.current || importStartedRef.current) {
      console.warn(
        '[FolderManager] Rescan already in progress or component unmounted',
      );
      return;
    }

    if (selectedFolders.size === 0) {
      console.warn('[FolderManager] No folders selected for rescan');
      return;
    }

    const selectedFoldersList = folders.filter((f) =>
      selectedFolders.has(f.id),
    );
    console.log('[FolderManager] Rescanning folders:', selectedFoldersList);

    importStartedRef.current = true;
    setIsAddingFolder(true);

    // Set initial progress
    const initialProgress: any = {
      // Changed to any as ImportProgressType is removed
      phase: 'scanning',
      source: {
        type: 'folder',
        name:
          selectedFolders.size === 1
            ? selectedFoldersList[0].name
            : `${selectedFolders.size} Folders`,
        paths: selectedFoldersList.map((f) => f.path),
      },
      totalTracks: 0,
      processedTracks: 0,
      message: 'Scanning folders and preparing rescan...',
    };

    // Remove any existing listener
    removeListener('track-import/progress');

    // Add flag to track if we've already processed completion
    let completionProcessed = false;

    // Set up progress listener
    const handleProgress = (progressData: any) => {
      // Changed to any as ImportProgressType is removed
      console.log('[FolderManager] Progress received:', progressData);

      if (!isMountedRef.current) {
        console.warn('[FolderManager] Component unmounted, ignoring progress');
        return;
      }

      // The progress UI is now handled by FolderList, so we just update the state
      // setProgress(progressData); // This line was removed as per the edit hint

      if (progressData.phase === 'complete' && !completionProcessed) {
        completionProcessed = true; // Mark as processed
        setIsAddingFolder(false); // Hide the adding folder indicator

        console.log(
          '[FolderManager] Rescan complete with progress data:',
          progressData,
        );
        const totalImported =
          (progressData.created || 0) + (progressData.merged || 0);

        console.log(
          '[FolderManager] Calculated totalImported:',
          totalImported,
          'from created:',
          progressData.created,
          'merged:',
          progressData.merged,
        );

        setTimeout(() => {
          if (onComplete) {
            // Trigger library refresh when folder rescan completes
            const refreshEvent = new CustomEvent('library-refresh', {
              detail: { reason: 'folder-rescan-completed' },
            });
            window.dispatchEvent(refreshEvent);

            console.log(
              '[FolderManager] Calling onComplete with totalImported:',
              totalImported,
            );
            onComplete(totalImported);
          }
        }, 3000);

        removeListener('track-import/progress');
      } else if (progressData.phase === 'complete' && completionProcessed) {
        console.log(
          '[FolderManager] Ignoring duplicate completion message:',
          progressData,
        );
      } else if (progressData.phase === 'error') {
        console.error('[FolderManager] Rescan error:', progressData.error);
        setIsAddingFolder(false); // Hide the adding folder indicator on error
        removeListener('track-import/progress');
      }
    };

    // on('track-import/progress', handleProgress); // This line was removed as per the edit hint

    // Use the unified TrackImportService
    const sourceData = {
      source: {
        type: 'folder' as const,
        name:
          selectedFolders.size === 1
            ? selectedFoldersList[0].name
            : `${selectedFolders.size} Folders`,
        paths: selectedFoldersList.map((f) => f.path),
      },
    };
    run('track-import/import-from-source', sourceData);
  };

  const handleSkipImport = () => {
    // If folders exist, treat as "Continue" and call onComplete
    // Otherwise, treat as "Skip for now" and call onSkip
    if (folders.length > 0 && onComplete) {
      onComplete(0);
    } else if (onSkip) {
      onSkip();
    }
  };

  const getTitle = () => {
    if (title) return title;

    // For wizard mode, use description as title if provided
    if (mode === 'wizard' && description) {
      return description;
    }

    switch (mode) {
      case 'wizard':
        return 'Add Your Music Folders';
      case 'library':
        return 'Import Music Folders';
      case 'management':
        return 'Manage Music Sources';
      default:
        return 'Folder Manager';
    }
  };

  const getDescription = () => {
    // For wizard mode, don't show description if it's being used as title
    if (mode === 'wizard' && description && !title) {
      return null;
    }

    if (description) return description;

    switch (mode) {
      case 'wizard':
        return 'Select folders containing your music files to build your library';
      case 'library':
        return 'Add new music folders to expand your library';
      case 'management':
        return 'Manage your music sources and import settings';
      default:
        return 'Select folders to import music files';
    }
  };

  // Show import progress UI instead of folder selection
  if (importStartedRef.current) {
    // Progress is now shown in-line for each folder, so we don't need this separate component
    return null;
  }

  // Main folder selection UI
  return (
    <div className={`folder-manager folder-manager--${mode} ${className}`}>
      {mode !== 'management' && (
        <div className="folder-manager__header">
          <h2>{getTitle()}</h2>
          {getDescription() && <p>{getDescription()}</p>}
        </div>
      )}

      {/* Folder Selection */}
      <div className="folder-manager__content">
        <FolderSelector
          folders={folders}
          selectedFolders={selectedFolders}
          onFoldersChange={setFolders}
          onSelectionChange={setSelectedFolders}
          onAddFolder={handleAddFolder}
          onRemoveFolder={handleRemoveFolder}
          isImporting={isAddingFolder}
          showAdvancedOptions={showAdvancedOptions}
          maxFolders={maxFolders}
        />

        {folders.length > 0 && (
          <FolderList
            folders={folders}
            selectedFolders={selectedFolders}
            onSelectionChange={setSelectedFolders}
            onRemoveFolder={handleRemoveFolder}
            onToggleWatching={handleToggleWatching}
            onRescanFolder={handleRescanFolder}
            allowBulkOperations={allowBulkOperations}
            showAdvancedOptions={showAdvancedOptions}
            isImporting={isAddingFolder}
          />
        )}
      </div>

      {/* Action Buttons */}
      <div className="folder-manager__actions">
        {/* Remove old import button - we now auto-import on folder selection */}

        {folders.length === 0 && (
          <div
            className="folder-manager__empty-state"
            onClick={handleAddFolder}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleAddFolder();
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="empty-state__icon">
              <IonIcon icon={folderOpenOutline} />
            </div>
            <h3>No folders added yet</h3>
            <IonButton
              fill="solid"
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleAddFolder();
              }}
              className="sources-btn"
              title="Find Music"
              style={{ marginTop: 12 }}
            >
              <IonIcon icon={folderOpenOutline} slot="start" /> Find Music
            </IonButton>
          </div>
        )}

        {showSkipOption && onSkip && (
          <>
            {folders.length > 0 ? (
              <>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleAddFolder}
                  style={{ marginBottom: '12px' }}
                >
                  <IonIcon icon={folderOpenOutline} slot="start" /> Add More
                  Folders
                </IonButton>
                <IonButton
                  expand="block"
                  className="next-button"
                  onClick={handleSkipImport}
                >
                  Continue <IonIcon slot="end" icon={arrowForward} />
                </IonButton>
              </>
            ) : (
              <div className="bottom-skip">
                <IonButton
                  expand="block"
                  fill="clear"
                  color="medium"
                  onClick={handleSkipImport}
                >
                  Skip for now
                </IonButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
